import { Box, Button, Chip, Divider, OutlinedInput, Typography } from '@mui/material'
import React from 'react'
import { i18n } from '../../index/index'
import { monthName } from './MonthlyTargetsTable'
import { MonthlyTargetValue } from './types'
import { useValueFormat } from './valueFormat'

export interface MonthlyTargetListItemProps {
  month: number
  monthlyTarget?: MonthlyTargetValue
  lastYearOutcome?: number
  lastYear: number
  hasLastYear: boolean
  isCurrentMonth: boolean
  isEditing: boolean
  unallocated: number
  isFocused: boolean
  onFocusMonth: (month: number | null) => void
  changeMonthlyTarget: (month: number, value: number) => void
  fillRemainderIntoMonth: (month: number) => void
}

/**
 * One month as a stacked list item (mobile Figma): month name (bold) with a small
 * "{lastYear}: X" sublabel on the left, and the editable target input on the right.
 * The current month is highlighted + "Nyt" chip. While editing, the "Lisää loput tähän"
 * hint follows the focused item and shows only when under-allocated (unallocated > 0).
 */
export function MonthlyTargetListItem({
  month,
  monthlyTarget,
  lastYearOutcome,
  lastYear,
  hasLastYear,
  isCurrentMonth,
  isEditing,
  unallocated,
  isFocused,
  onFocusMonth,
  changeMonthlyTarget,
  fillRemainderIntoMonth,
}: MonthlyTargetListItemProps) {
  const { format } = useValueFormat()
  const target = monthlyTarget?.value ?? 0
  const showHint = isFocused && unallocated > 0

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          py: 1,
          px: 1,
          ...(isCurrentMonth
            ? {
                backgroundColor: (theme) => theme.palette.currentMonth.tint,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              }
            : {}),
        }}
      >
        <Box sx={{ minWidth: 0, flexShrink: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant='body1' fontWeight={600}>
              {capitalize(monthName(month))}
            </Typography>
            {isCurrentMonth && (
              <Chip label={i18n.t('admin.monthlyTargets.currentMonthChip')} size='small' color='success' />
            )}
          </Box>
          {hasLastYear && (
            <Typography variant='caption' color='text.secondary'>
              {lastYear}: {lastYearOutcome !== undefined ? format(lastYearOutcome) : '-'}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
          {!monthlyTarget ? (
            <Typography variant='body1'>-</Typography>
          ) : isEditing ? (
            <>
              <OutlinedInput
                size='small'
                type='number'
                value={target || ''}
                onFocus={() => onFocusMonth(month)}
                onBlur={() => onFocusMonth(null)}
                onChange={(event) => changeMonthlyTarget(month, Number(event.target.value) || 0)}
                sx={{
                  width: 100,
                  ...(isCurrentMonth ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main' } } : {}),
                }}
                inputProps={{ style: { textAlign: 'right' } }}
              />
              {showHint && (
                <Button
                  type='button'
                  variant='outlined'
                  color='success'
                  size='small'
                  // onMouseDown (not onClick) so the input's onBlur doesn't hide the hint
                  // before the click registers.
                  onMouseDown={(event) => {
                    event.preventDefault()
                    fillRemainderIntoMonth(month)
                  }}
                  sx={{ mt: 0.5, borderRadius: 5, textTransform: 'none', fontSize: '0.7rem', py: 0.1 }}
                >
                  {i18n.t('admin.monthlyTargets.addRemainderHere', { remainder: format(unallocated) })}
                </Button>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
              <Typography variant='body1'>{format(target)}</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Divider />
    </>
  )
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
